/**
 * One card per Legacy (3-slide) post draft. Status-driven action row,
 * mirroring the Beta (AI-image) card so both pipelines feel identical:
 *
 *   pending        → Copy slide · Regenerate text · With a note · Publish to Buffer · Discard
 *   scheduled      → "Scheduled" + Copy slide · Publish to Buffer (re-schedule) · Discard
 *   published      → "Published ✓" + Copy slide · Schedule again · Discard
 *   *-failed       → error + Regenerate text · With a note · Discard
 *
 * Kept decoupled from Beta: its own PublishModal + api calls (no
 * cross-imports), adapted to the Legacy pipeline's 3 slides
 * (cover / ar-body / en-body). The Publish modal is the real scheduler —
 * channel toggles (IG / Twitter / Pinterest), editable caption, Schedule
 * vs Publish-now, brand date picker, per-channel results — replacing the
 * old one-click "Approve & Schedule" that hard-coded 24h / IG-only.
 */
import { useEffect, useRef, useState } from "react";
import { CalendarClock, Check, ChevronLeft, ChevronRight, Copy, Loader2, MessageSquarePlus, Minus, Plus, Repeat, Send, Sparkles, Trash2, X } from "lucide-react";
import { useIsMobile } from "../lib/useIsMobile";
import { BrandDateTimePicker } from "./BrandDateTimePicker";
import { ConfirmDialog } from "./ConfirmDialog";
import { DestinationToggle, DestinationEmpty } from "./DestinationToggle";
import { RescheduleEditor } from "./RescheduleEditor";
import {
  type PostDraft,
  type PostPublishResult,
  type SchedulingChannel,
  type CtaConfig,
  adjustPostQuoteLength,
  postSlideUrl,
  regeneratePostDraft,
  discardPostDraft,
  duplicatePostDraft,
  publishPostDraft,
  rescheduleDraft,
  getPostCaptionTemplate,
  getCtaConfig,
  ctaSlideUrl,
  listSchedulingChannels,
} from "../api";

const SLIDES = ["cover", "ar-body", "en-body"] as const;
type Slide = (typeof SLIDES)[number];

// Services that can receive an image carousel, in display order. Curated to
// image-capable networks — matches the backend service groups in
// postDraftsRoutes.ts. (The VIDEO schedule modal lists every connected Buffer
// channel; a carousel only goes to these.) Adding a future image-capable
// platform = one entry here + its backend group.
const CAROUSEL_SERVICES: SchedulingChannel["service"][] = [
  "instagram",
  "twitter",
  "pinterest",
  "linkedin",
];
const SLIDE_LABEL: Record<Slide, string> = {
  cover: "Cover",
  "ar-body": "Arabic",
  "en-body": "English",
};

// Status pill colour + label — mirrors the AI-image (Beta) card so both
// pipelines read identically. "pending" is the Legacy equivalent of Beta's
// "ready-for-review" (rendered, awaiting operator action) → green READY.
const statusColor: Record<PostDraft["status"], string> = {
  "generating-text": "rgba(255,213,0,.85)",
  pending: "rgba(74,222,128,.85)",
  scheduled: "rgba(74,222,128,.65)",
  published: "rgba(180,180,180,.65)",
  discarded: "rgba(255,255,255,.35)",
  "generation-failed": "rgba(248,113,113,.85)",
  "render-failed": "rgba(248,113,113,.85)",
};
const statusLabel: Record<PostDraft["status"], string> = {
  "generating-text": "Writing…",
  pending: "Ready",
  scheduled: "Scheduled",
  published: "Published",
  discarded: "Discarded",
  "generation-failed": "Failed",
  "render-failed": "Failed",
};

interface Props {
  draft: PostDraft;
  onChange: () => void;
  /** Server flag (QL_POST_LENGTH_CONTROL). Off ⇒ no Shorter/Longer buttons,
   *  so a disabled route can never surface as a broken button. */
  lengthControl?: boolean;
  /** "Schedule again" (2026-09-14): the server made a fresh pending COPY of
   *  this published post — hand it to the page so it can show it and open
   *  its Publish modal. Absent ⇒ the button is hidden. */
  onRecycled?: (copy: PostDraft) => void;
  /** Open the Publish modal on mount — set by the page on the card of a
   *  just-recycled copy, so "Schedule again" lands straight in the scheduler. */
  autoOpenPublish?: boolean;
}

export function PostCard({ draft, onChange, lengthControl = false, onRecycled, autoOpenPublish = false }: Props): React.JSX.Element {
  const [busy, setBusy] = useState<null | "regenerate" | "discard" | "shorter" | "longer" | "recycle">(null);
  // A direction the server has told us this quote can't move any further.
  // Learned from its 409 rather than by duplicating the length bands in the
  // client — the server stays the single source of truth. Cleared whenever
  // the quote actually changes.
  const [blocked, setBlocked] = useState<{ shorter?: string; longer?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  // "Regenerate with a note" — a collapsible textarea whose contents are
  // injected into the generation prompt for a steered re-roll.
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  // Current slide (controlled SlideCarousel) so "Copy slide N" knows which
  // PNG to put on the clipboard.
  const [slideIdx, setSlideIdx] = useState(0);
  const [copyState, setCopyState] = useState<"idle" | "copying" | "copied" | "error">("idle");
  const [publishOpen, setPublishOpen] = useState(false);
  const [reschedOpen, setReschedOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  // Cache-buster for the slide <img>s. Regenerate overwrites the PNGs at the
  // same URL, so without bumping this the browser shows the stale cached
  // image until a hard refresh. Mirrors the Beta card's cacheKey machinery.
  const [cacheKey, setCacheKey] = useState(0);

  // Background actions (2026-08-19) finish AFTER the POST returns — the fresh
  // slides arrive via polling, not via a local await. Watch the draft's save
  // stamp and bust the image cache whenever it moves, whoever moved it.
  const saveStamp = draft.generatedAt;
  const prevStampRef = useRef(saveStamp);
  useEffect(() => {
    if (prevStampRef.current !== saveStamp) {
      prevStampRef.current = saveStamp;
      setCacheKey(Date.now());
      // A fresh quote has its own room to grow or shrink again.
      setBlocked({});
    }
  }, [saveStamp]);

  // The server-persisted action marker. While set, every mutating button on
  // this card is disabled — and unlike the local `busy` state, this survives
  // refresh/navigation, because it lives on the draft itself.
  const serverBusy = draft.busy ?? null;
  const anyBusy = busy !== null || serverBusy !== null;

  // While a background action holds the draft, the status pill reports THAT,
  // not the stored status — a card that says READY while its text is being
  // rewritten invites publishing content that's about to change (operator
  // request 2026-08-19). Amber, with a spinner, like the Beta transients.
  const BUSY_PILL: Record<NonNullable<PostDraft["busy"]>["action"], string> = {
    generate: "Writing…",
    regenerate: "Rewriting…",
    "quote-shorter": "Re-cutting…",
    "quote-longer": "Re-cutting…",
  };
  const pillLabel = serverBusy ? BUSY_PILL[serverBusy.action] : statusLabel[draft.status];
  const pillColor = serverBusy ? "rgba(255,213,0,.85)" : statusColor[draft.status];

  const isScheduled = draft.status === "scheduled";
  const isPublished = draft.status === "published";
  const isFailed =
    draft.status === "generation-failed" || draft.status === "render-failed";
  // Slides exist on disk for any non-failed draft (failed drafts were saved
  // with empty PNGs, so Copy / Publish would only frustrate).
  const hasSlides = draft.status === "pending" || isScheduled || isPublished;
  const canPublish = draft.status === "pending" || isScheduled;
  const canRegenerate = draft.status === "pending" || isFailed;

  const handleRegenerate = async (feedback?: string): Promise<void> => {
    // 202 flow (2026-08-19): the POST returns as soon as the server stamps the
    // draft busy; the re-roll runs in the background and lands via polling
    // (the saveStamp effect busts the image cache when it does). Navigating
    // away cancels nothing.
    setBusy("regenerate");
    setErr(null);
    try {
      await regeneratePostDraft(draft.id, feedback ? { feedback } : {});
      setNote("");
      setNoteOpen(false);
      onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Re-cut the cover quote ~15% shorter or longer. The reflection body is
   * never touched. When the server says this quote has hit the end of its
   * render-safe range, remember that direction so the button greys out
   * instead of inviting a click that can only fail again.
   */
  const handleLength = async (direction: "shorter" | "longer"): Promise<void> => {
    // 202 flow (2026-08-19): the rewrite + re-render run in the background;
    // progress shows from the server-persisted busy marker via polling, and
    // the outcome (including the "Kept your quote — …" reason) arrives as a
    // persisted notice on the draft. The only errors this catch still sees
    // are the SYNCHRONOUS ones: at-the-band-edge and double-press 409s.
    setBusy(direction);
    setErr(null);
    try {
      await adjustPostQuoteLength(draft.id, direction);
      onChange();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      if (/already as (short|long)/i.test(msg)) {
        setBlocked((b) => ({ ...b, [direction]: msg }));
      }
    } finally {
      setBusy(null);
    }
  };

  /**
   * Copy the currently visible slide PNG to the clipboard so the operator
   * can paste it straight into Instagram, Buffer, etc. Cache-busts the
   * fetch so a copy right after a Regenerate grabs the fresh render.
   */
  const handleCopySlide = async (): Promise<void> => {
    setCopyState("copying");
    setErr(null);
    try {
      const url = `${postSlideUrl(draft.id, SLIDES[slideIdx])}?t=${Date.now()}`;
      const r = await fetch(url, { credentials: "same-origin" });
      if (!r.ok) throw new Error(`fetch failed: ${r.status}`);
      const blob = await r.blob();
      if (typeof ClipboardItem === "undefined") {
        throw new Error("ClipboardItem not supported in this browser");
      }
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1800);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setCopyState("error");
      setTimeout(() => setCopyState("idle"), 2500);
    }
  };

  const handleDiscard = (): void => setConfirmDiscardOpen(true);
  const confirmDiscard = async (): Promise<void> => {
    setConfirmDiscardOpen(false);
    setBusy("discard");
    setErr(null);
    try {
      await discardPostDraft(draft.id);
      onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  // A just-recycled copy opens its scheduler immediately (once, on mount).
  useEffect(() => {
    if (autoOpenPublish) setPublishOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design
  }, []);

  const handleRecycle = async (): Promise<void> => {
    if (!onRecycled) return;
    setBusy("recycle");
    setErr(null);
    try {
      onRecycled(await duplicatePostDraft(draft.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const slideUrl = (i: number): string =>
    `${postSlideUrl(draft.id, SLIDES[i])}?t=${cacheKey}`;

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          padding: 18,
          borderRadius: 18,
          border: "1px solid rgba(255,255,255,.06)",
          background: "rgba(16,18,27,.72)",
        }}
      >
        {/* Header: title + status badge — at parity with the AI-image card. */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ql-fg)" }}>
              {draft.theme}
            </div>
            <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)" }}>
              generated {fmtRel(draft.generatedAt)}
              {draft.scheduledFor && ` · for ${new Date(draft.scheduledFor).toLocaleString()}`}
              {draft.recycledFrom && (
                <span
                  title={`Scheduled again from ${draft.recycledFrom}`}
                  style={{ marginLeft: 8, color: "var(--ql-yellow)", fontWeight: 700 }}
                >
                  · recycled
                </span>
              )}
            </div>
            {isScheduled && draft.scheduledFor && (
              <button
                type="button"
                onClick={() => setReschedOpen((v) => !v)}
                title="Change the date & time"
                style={{
                  alignSelf: "flex-start",
                  marginTop: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "3px 9px",
                  borderRadius: 7,
                  border: reschedOpen ? "1px solid rgba(255,213,0,.5)" : "1px solid rgba(255,255,255,.14)",
                  background: reschedOpen ? "rgba(255,213,0,.10)" : "transparent",
                  color: reschedOpen ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <CalendarClock size={11} strokeWidth={2} /> Edit time
              </button>
            )}
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: "rgba(255,255,255,.04)",
              border: `1px solid ${pillColor}`,
              color: pillColor,
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {serverBusy && <Loader2 size={11} className="ql-spin" />}
            {pillLabel}
          </span>
        </div>

        {isScheduled && reschedOpen && draft.scheduledFor && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,213,0,.22)",
              background: "rgba(255,213,0,.04)",
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ql-fg)" }}>Change date &amp; time</div>
            <RescheduleEditor
              currentISO={draft.scheduledFor}
              onSave={async (iso) => {
                const { errors } = await rescheduleDraft(draft.id, iso);
                setReschedOpen(false);
                onChange();
                if (errors.length) {
                  setErr(
                    `Rescheduled, but ${errors.length} channel${errors.length === 1 ? "" : "s"} didn't move: ${errors
                      .map((e) => e.error)
                      .join("; ")}`,
                  );
                }
              }}
              onCancel={() => setReschedOpen(false)}
              saveLabel="Save time"
              note={
                draft.bufferPostIds && draft.bufferPostIds.length > 1
                  ? `Moves all ${draft.bufferPostIds.length} channels of this post together.`
                  : undefined
              }
            />
          </div>
        )}

        {/* Slide preview — polished viewer (peek-deck depth, chevrons, 1/N
            counter, dot selector) at parity with the AI-image card. */}
        <div
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "4 / 5",
            borderRadius: 14,
            overflow: "hidden",
            background: "#0A0908",
            border: "1px solid rgba(255,255,255,.06)",
          }}
        >
          {hasSlides ? (
            <>
              {/* Peek-deck depth — the upcoming slides ghosted behind the
                  active one. Decorative; pointer-events none. */}
              {[2, 1].map((off) => {
                const ghostIdx = (slideIdx + off) % SLIDES.length;
                return (
                  <img
                    key={off}
                    src={slideUrl(ghostIdx)}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    style={{
                      position: "absolute",
                      inset: 14 + off * 6,
                      width: `calc(100% - ${(14 + off * 6) * 2}px)`,
                      height: `calc(100% - ${(14 + off * 6) * 2}px)`,
                      objectFit: "cover",
                      borderRadius: 10,
                      transform: `scale(${1 - off * 0.04}) translateY(${-off * 8}px)`,
                      opacity: 0.3 - off * 0.1,
                      filter: "blur(2px)",
                      zIndex: off === 1 ? 1 : 0,
                      pointerEvents: "none",
                    }}
                  />
                );
              })}
              <img
                src={slideUrl(slideIdx)}
                alt={`Slide ${slideIdx + 1}`}
                loading="lazy"
                decoding="async"
                style={{
                  position: "relative",
                  zIndex: 2,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  boxShadow: "0 6px 20px rgba(0,0,0,.45)",
                }}
              />
              <button
                type="button"
                aria-label="Previous slide"
                title="Previous slide"
                onClick={() => setSlideIdx((slideIdx - 1 + SLIDES.length) % SLIDES.length)}
                style={chevronStyle("left")}
              >
                <ChevronLeft size={20} strokeWidth={2.5} />
              </button>
              <button
                type="button"
                aria-label="Next slide"
                title="Next slide"
                onClick={() => setSlideIdx((slideIdx + 1) % SLIDES.length)}
                style={chevronStyle("right")}
              >
                <ChevronRight size={20} strokeWidth={2.5} />
              </button>
              <div
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 4,
                  padding: "4px 10px",
                  background: "rgba(0,0,0,.55)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "var(--ql-font-mono)",
                  borderRadius: 999,
                  pointerEvents: "none",
                  letterSpacing: ".06em",
                  backdropFilter: "blur(4px)",
                }}
              >
                {slideIdx + 1} / {SLIDES.length}
              </div>
              {/* Work-in-progress veil. Keys on the SERVER busy marker, not
                  the local click state — since the 202 change the POST
                  returns in under a second and the real work happens in the
                  background, so the local flag would clear while the slides
                  are still being rewritten. (busy === "regenerate" is kept
                  for the sub-second window before the first poll.) */}
              {(serverBusy !== null || busy === "regenerate") && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(10,9,8,.72)",
                    backdropFilter: "blur(2px)",
                    gap: 12,
                    zIndex: 3,
                  }}
                >
                  <Loader2 size={32} className="ql-spin" color="var(--ql-yellow)" />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      color: "var(--ql-yellow)",
                      fontFamily: "var(--ql-font-mono)",
                    }}
                  >
                    {serverBusy ? BUSY_PILL[serverBusy.action] : "Rewriting…"}
                  </span>
                </div>
              )}
              {/* Slide selector dots */}
              <div
                style={{
                  position: "absolute",
                  bottom: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 6,
                  zIndex: 4,
                }}
              >
                {SLIDES.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSlideIdx(i)}
                    title={SLIDE_LABEL[s]}
                    aria-label={`Show ${SLIDE_LABEL[s]} slide`}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: i === slideIdx ? "var(--ql-yellow)" : "rgba(255,255,255,.35)",
                      border: 0,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            /* Failed / no-slides on disk — nothing to preview. */
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: 24,
                textAlign: "center",
                background: "radial-gradient(ellipse at center, rgba(248,113,113,.10) 0%, rgba(0,0,0,0) 65%)",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--ql-danger)",
                  fontFamily: "var(--ql-font-mono)",
                }}
              >
                {isFailed ? "Generation failed" : "No slides yet"}
              </div>
              <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", lineHeight: 1.5, maxWidth: 240 }}>
                {isFailed
                  ? "No slides were rendered. Use Regenerate text (or With a note) to try again."
                  : "Slides will appear here once the post finishes rendering."}
              </div>
            </div>
          )}
        </div>

        {draft.errorMessage && (
          <div
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(248,113,113,.10)",
              border: "1px solid rgba(248,113,113,.25)",
              color: "#FCA5A5",
              fontSize: 11,
              lineHeight: 1.4,
            }}
          >
            {draft.errorMessage}
          </div>
        )}
        {err && <div style={{ fontSize: 11, color: "#FCA5A5" }}>{err}</div>}
        {/* Outcome of the last background action — server-persisted, so it's
            still here after a refresh or a page switch (2026-08-19). Hidden
            while a new action runs; that action will write its own. */}
        {!err && !serverBusy && draft.notice && (
          <div
            style={{
              fontSize: 11,
              color: /failed|could not/i.test(draft.notice.message)
                ? "#FCA5A5"
                : "var(--ql-fg-muted)",
              lineHeight: 1.5,
            }}
          >
            {draft.notice.message}
          </div>
        )}

        {/* Action row — mirrors the Beta card's set. */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {hasSlides && (
            <button
              type="button"
              onClick={() => void handleCopySlide()}
              disabled={anyBusy || copyState === "copying"}
              title={`Copy the ${SLIDE_LABEL[SLIDES[slideIdx]]} slide PNG to the clipboard`}
              style={btnGhost(copyState === "copied")}
            >
              {copyState === "copying" ? (
                <Loader2 size={13} className="ql-spin" />
              ) : copyState === "copied" ? (
                <Check size={13} strokeWidth={2.4} color="#7BD389" />
              ) : (
                <Copy size={13} strokeWidth={2} />
              )}
              {copyState === "copied"
                ? "Copied!"
                : copyState === "error"
                  ? "Failed"
                  : `Copy slide ${slideIdx + 1}`}
            </button>
          )}
          {canRegenerate && (
            <button
              type="button"
              onClick={() => void handleRegenerate()}
              disabled={anyBusy}
              title="Regenerate a fresh version (same theme/seed)"
              style={btnGhost((busy === "regenerate" || serverBusy?.action === "regenerate") && !noteOpen)}
            >
              {(busy === "regenerate" || serverBusy?.action === "regenerate") && !noteOpen ? (
                <Loader2 size={13} className="ql-spin" />
              ) : (
                <Sparkles size={13} strokeWidth={2} />
              )}
              Regenerate text
            </button>
          )}
          {canRegenerate && (
            <button
              type="button"
              onClick={() => setNoteOpen((v) => !v)}
              disabled={anyBusy}
              title="Regenerate with a note — tell it what to change"
              style={btnGhost(noteOpen)}
            >
              {noteOpen ? (
                <X size={13} strokeWidth={2} />
              ) : (
                <MessageSquarePlus size={13} strokeWidth={2} />
              )}
              With a note
            </button>
          )}
          {canRegenerate && lengthControl && (
            <>
              <button
                type="button"
                onClick={() => void handleLength("shorter")}
                disabled={anyBusy || blocked.shorter !== undefined}
                title={
                  blocked.shorter ??
                  "Re-cut the quote about 15% shorter — same meaning, reflection untouched"
                }
                style={btnGhost(busy === "shorter" || serverBusy?.action === "quote-shorter")}
              >
                {busy === "shorter" || serverBusy?.action === "quote-shorter" ? (
                  <Loader2 size={13} className="ql-spin" />
                ) : (
                  <Minus size={13} strokeWidth={2} />
                )}
                Shorter
              </button>
              <button
                type="button"
                onClick={() => void handleLength("longer")}
                disabled={anyBusy || blocked.longer !== undefined}
                title={
                  blocked.longer ??
                  "Re-cut the quote about 15% longer — same meaning, reflection untouched"
                }
                style={btnGhost(busy === "longer" || serverBusy?.action === "quote-longer")}
              >
                {busy === "longer" || serverBusy?.action === "quote-longer" ? (
                  <Loader2 size={13} className="ql-spin" />
                ) : (
                  <Plus size={13} strokeWidth={2} />
                )}
                Longer
              </button>
            </>
          )}
          {canPublish && (
            <button
              type="button"
              onClick={() => setPublishOpen(true)}
              disabled={anyBusy}
              title="Push these 3 slides to Buffer (Instagram, Twitter, Pinterest, LinkedIn)"
              style={{
                ...btnGhost(false),
                borderColor: "rgba(123,211,137,.35)",
                color: "#7BD389",
                background: "rgba(123,211,137,.08)",
              }}
            >
              <Send size={13} strokeWidth={2} />
              Publish to Buffer
            </button>
          )}
          {isPublished && onRecycled && (
            <button
              type="button"
              onClick={() => void handleRecycle()}
              disabled={anyBusy}
              title="Put this post back on the calendar: makes a fresh copy with the same slides and opens the scheduler. The original stays Published."
              style={{
                ...btnGhost(busy === "recycle"),
                borderColor: "rgba(123,211,137,.35)",
                color: "#7BD389",
                background: "rgba(123,211,137,.08)",
              }}
            >
              {busy === "recycle" ? (
                <Loader2 size={13} className="ql-spin" />
              ) : (
                <Repeat size={13} strokeWidth={2} />
              )}
              Schedule again
            </button>
          )}
          <button
            type="button"
            onClick={handleDiscard}
            disabled={anyBusy}
            style={{
              ...btnGhost(busy === "discard"),
              color: "var(--ql-danger)",
              borderColor: "rgba(248,113,113,.25)",
            }}
          >
            {busy === "discard" ? (
              <Loader2 size={13} className="ql-spin" />
            ) : (
              <Trash2 size={13} strokeWidth={2} />
            )}
            Discard
          </button>
        </div>

        {noteOpen && canRegenerate && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What should change? e.g. “make the Arabic punchier”, “less abstract”, “keep the hook, redo the reflection”."
              rows={2}
              disabled={anyBusy}
              style={{
                resize: "vertical",
                width: "100%",
                boxSizing: "border-box",
                padding: "9px 11px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,.12)",
                background: "rgba(0,0,0,.22)",
                color: "var(--ql-fg)",
                fontSize: 12.5,
                lineHeight: 1.45,
                fontFamily: "inherit",
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => void handleRegenerate(note)}
                disabled={anyBusy || note.trim().length === 0}
                style={{
                  ...btnGhost(busy === "regenerate"),
                  borderColor: "rgba(255,213,0,.30)",
                  color: "var(--ql-yellow)",
                  background: "rgba(255,213,0,.08)",
                  opacity: anyBusy || note.trim().length === 0 ? 0.5 : 1,
                }}
              >
                {busy === "regenerate" ? (
                  <Loader2 size={13} className="ql-spin" />
                ) : (
                  <Sparkles size={13} strokeWidth={2} />
                )}
                Regenerate with this note
              </button>
              <button
                type="button"
                onClick={() => {
                  setNote("");
                  setNoteOpen(false);
                }}
                disabled={anyBusy}
                style={btnGhost(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>

      {publishOpen && (
        <PublishModal
          draft={draft}
          onClose={() => setPublishOpen(false)}
          onPublished={() => {
            setPublishOpen(false);
            onChange();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDiscardOpen}
        title={`Discard draft "${draft.theme}"?`}
        message="This moves the draft to the discarded bucket on disk and removes it from the queue."
        confirmLabel="Discard draft"
        danger
        busy={busy === "discard"}
        onConfirm={() => void confirmDiscard()}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
}

/**
 * Publish-to-Buffer modal for the Legacy pipeline. Mirrors the Beta
 * PublishModal, adapted to 3 slides:
 *   - Instagram + Twitter: full 3-slide carousel (cover → ar-body → en-body)
 *   - Pinterest: first slide only (Pinterest doesn't carousel)
 *   - Instagram goes out in Buffer's "Notification" mode (phone push at
 *     dueAt; operator adds music before posting) — set server-side.
 *
 * Caption defaults to the sign-off template; the operator edits it freely
 * before publishing. "Publish now" schedules ~60 s out (Buffer rejects
 * exact-now) and the server marks it Published optimistically.
 */
function defaultDueAt(): string {
  // Tomorrow at 09:30 local — time to review, and well clear of "now".
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 30, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

function PublishModal({
  draft,
  onClose,
  onPublished,
}: {
  draft: PostDraft;
  onClose: () => void;
  onPublished: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const [channels, setChannels] = useState<SchedulingChannel[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Channels the operator flipped to Buffer's notification mode (phone
  // push at dueAt, finish manually). Everything else direct-publishes.
  const [notifyIds, setNotifyIds] = useState<Set<string>>(new Set());
  const [caption, setCaption] = useState<string>("");
  const [dueAt, setDueAt] = useState<string>(defaultDueAt());
  const [publishNow, setPublishNow] = useState<boolean>(false);
  // CTA appendix (Settings → CTA slides). Loaded fail-soft — a broken CTA
  // store must never block the publish modal.
  const [ctaCfg, setCtaCfg] = useState<CtaConfig | null>(null);
  const [skipCta, setSkipCta] = useState<boolean>(false);
  const [previewSlide, setPreviewSlide] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<PostPublishResult[] | null>(null);

  // Lock body scroll + ESC to close while the modal is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, busy]);

  // Load caption template + channels in parallel on mount.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getPostCaptionTemplate(), listSchedulingChannels()])
      .then(([tpl, chans]) => {
        if (cancelled) return;
        const topic = draft.theme.charAt(0).toUpperCase() + draft.theme.slice(1);
        setCaption(tpl.replace(/\{\{topic\}\}/g, topic));
        setChannels(chans);
        // Pre-select all IG / Twitter / Pinterest / LinkedIn channels;
        // operator can toggle any off.
        const pre = new Set<string>();
        for (const c of chans) {
          if (CAROUSEL_SERVICES.includes(c.service)) {
            pre.add(c.id);
          }
        }
        setSelected(pre);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    getCtaConfig()
      .then((c) => {
        if (!cancelled) setCtaCfg(c);
      })
      .catch(() => {
        /* CTA info is advisory — the strip just doesn't render */
      });
    return () => {
      cancelled = true;
    };
  }, [draft.theme]);

  const toggle = (id: string): void => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleNotify = (id: string): void => {
    setNotifyIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const handlePublish = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      // "Publish now" → ~60 s in the future (Buffer rejects exact-now).
      const iso = publishNow
        ? new Date(Date.now() + 60_000).toISOString()
        : new Date(dueAt).toISOString();
      const scheduling: Record<string, "notification"> = {};
      for (const id of notifyIds) if (selected.has(id)) scheduling[id] = "notification";
      const res = await publishPostDraft(draft.id, {
        caption,
        dueAt: iso,
        channelIds: Array.from(selected),
        scheduling,
        ...(skipCta ? { skipCta: true } : {}),
      });
      setResults(res.results);
      // Close & refresh only if every channel succeeded; otherwise keep the
      // modal open so the operator can read the per-channel error and retry.
      const allOk = res.results.length > 0 && res.results.every((r) => r.postId);
      if (allOk) setTimeout(onPublished, 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const byService = (svc: string): SchedulingChannel[] =>
    (channels ?? []).filter((c) => c.service === svc);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,.72)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: isMobile ? "calc(100vw - 24px)" : 980,
          maxHeight: "92vh",
          background: "rgba(16,18,27,.96)",
          border: "1px solid rgba(255,255,255,.10)",
          borderRadius: 18,
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,.55)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? "12px 14px" : "16px 22px",
            borderBottom: "1px solid rgba(255,255,255,.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <div
              style={{
                fontSize: 10,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "#7BD389",
                fontWeight: 700,
              }}
            >
              Publish to Buffer
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "var(--ql-fg)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {draft.theme}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!busy) onClose();
            }}
            disabled={busy}
            aria-label="Close publish"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.04)",
              color: "var(--ql-fg)",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Body: 2 columns — left = slide preview, right = form */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "minmax(240px, 1fr) minmax(360px, 2fr)",
            overflow: isMobile ? "auto" : "hidden",
          }}
        >
          <div
            style={{
              padding: isMobile ? 14 : 22,
              borderRight: isMobile ? "none" : "1px solid rgba(255,255,255,.06)",
              borderBottom: isMobile ? "1px solid rgba(255,255,255,.06)" : "none",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
              justifyContent: "flex-start",
              overflow: "auto",
            }}
          >
            <img
              src={postSlideUrl(draft.id, SLIDES[previewSlide])}
              alt={`Slide ${previewSlide + 1} preview`}
              style={{
                width: "100%",
                maxWidth: 320,
                aspectRatio: "4 / 5",
                objectFit: "contain",
                borderRadius: 14,
                background: "#0A0908",
                border: "1px solid rgba(255,255,255,.06)",
              }}
            />
            {/* Slot pills */}
            <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
              {SLIDES.map((s, i) => {
                const active = i === previewSlide;
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setPreviewSlide(i)}
                    title={SLIDE_LABEL[s]}
                    style={{
                      padding: "5px 11px",
                      borderRadius: 999,
                      border: `1px solid ${active ? "rgba(123,211,137,.50)" : "rgba(255,255,255,.10)"}`,
                      background: active ? "rgba(123,211,137,.16)" : "rgba(255,255,255,.03)",
                      color: active ? "#7BD389" : "var(--ql-fg-subtle)",
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: "var(--ql-font-mono)",
                      cursor: "pointer",
                    }}
                    aria-label={`Show ${SLIDE_LABEL[s]} slide`}
                  >
                    {i + 1} · {SLIDE_LABEL[s]}
                  </button>
                );
              })}
            </div>
            <div
              style={{
                fontSize: 10,
                letterSpacing: ".10em",
                textTransform: "uppercase",
                color: "var(--ql-fg-subtle)",
                fontWeight: 700,
              }}
            >
              Slide {previewSlide + 1} of {SLIDES.length}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--ql-fg-muted)",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              Instagram, Twitter &amp; LinkedIn receive all 3 slides as a
              carousel. Pinterest receives only slide&nbsp;1.
            </div>
            {(() => {
              // CTA appendix strip — informational; publishing needs no
              // interaction. Renders only when the feature is on and a CTA
              // is next in rotation.
              const nextCta =
                ctaCfg?.enabled && ctaCfg.next
                  ? (ctaCfg.ctas.find((c) => c.id === ctaCfg.next?.id) ?? null)
                  : null;
              if (!nextCta) return null;
              const dests = (["instagram", "linkedin", "twitter"] as const)
                .filter((s) => ctaCfg?.platforms[s])
                // Twitter caps at 4 images — a 2-slide CTA (3+2) never fits.
                .filter((s) => !(s === "twitter" && nextCta.slides.length > 1))
                .map((s) => (s === "twitter" ? "Twitter" : s === "linkedin" ? "LinkedIn" : "Instagram"));
              return (
                <div
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: skipCta ? "rgba(255,255,255,.02)" : "rgba(255,213,0,.05)",
                    border: skipCta
                      ? "1px solid rgba(255,255,255,.08)"
                      : "1px solid rgba(255,213,0,.22)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: skipCta ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                    {nextCta.slides.map((s) => (
                      <img
                        key={s}
                        src={ctaSlideUrl(nextCta.id, s)}
                        alt=""
                        loading="lazy"
                        style={{
                          width: 30,
                          height: 38,
                          objectFit: "cover",
                          borderRadius: 4,
                          border: "1px solid rgba(255,255,255,.12)",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ql-fg)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      CTA: {nextCta.label}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--ql-fg-muted)", marginTop: 1 }}>
                      {skipCta
                        ? "Skipped for this post"
                        : `+${nextCta.slides.length} slide${nextCta.slides.length > 1 ? "s" : ""} → ${dests.join(", ") || "no platform ticked"}`}
                    </div>
                  </div>
                  <label
                    style={{
                      fontSize: 10,
                      color: "var(--ql-fg-muted)",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={skipCta}
                      onChange={(e) => setSkipCta(e.target.checked)}
                    />
                    Skip
                  </label>
                </div>
              );
            })()}
          </div>

          <div
            style={{
              padding: isMobile ? 14 : 22,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {/* Channel toggles */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: ".10em",
                  textTransform: "uppercase",
                  color: "var(--ql-fg-subtle)",
                  fontWeight: 700,
                }}
              >
                Destinations
              </div>
              {channels === null ? (
                <div style={{ color: "var(--ql-fg-muted)", fontSize: 12 }}>Loading channels…</div>
              ) : (
                CAROUSEL_SERVICES.map((svc) => {
                  const list = byService(svc);
                  if (list.length === 0) return <DestinationEmpty key={svc} service={svc} />;
                  const meta = svc === "pinterest" ? "slide 1 only" : "all 3 slides";
                  return list.map((c) => (
                    <DestinationToggle
                      key={c.id}
                      service={c.service}
                      name={c.name}
                      meta={meta}
                      selected={selected.has(c.id)}
                      onToggle={() => toggle(c.id)}
                      disabled={busy}
                      notify={notifyIds.has(c.id)}
                      onNotifyToggle={() => toggleNotify(c.id)}
                    />
                  ));
                })
              )}
            </div>

            {/* Schedule time */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span
                style={{
                  fontSize: 10,
                  letterSpacing: ".10em",
                  textTransform: "uppercase",
                  color: "var(--ql-fg-subtle)",
                  fontWeight: 700,
                }}
              >
                When
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setPublishNow(false)}
                  disabled={busy}
                  style={whenBtn(!publishNow, busy)}
                >
                  Schedule
                </button>
                <button
                  type="button"
                  onClick={() => setPublishNow(true)}
                  disabled={busy}
                  style={whenBtn(publishNow, busy)}
                >
                  Publish now
                </button>
              </div>
              {!publishNow ? (
                <BrandDateTimePicker value={dueAt} onChange={setDueAt} disabled={busy} />
              ) : (
                <div
                  style={{
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px dashed rgba(123,211,137,.30)",
                    background: "rgba(123,211,137,.04)",
                    color: "var(--ql-fg-muted)",
                    fontSize: 11,
                    fontFamily: "var(--ql-font-mono)",
                    lineHeight: 1.5,
                  }}
                >
                  Schedules for ~1 minute from now (Buffer rejects exact-now).
                  Publishes on the next queue tick — essentially immediate.
                </div>
              )}
            </div>

            {/* Caption */}
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span
                style={{
                  fontSize: 10,
                  letterSpacing: ".10em",
                  textTransform: "uppercase",
                  color: "var(--ql-fg-subtle)",
                  fontWeight: 700,
                }}
              >
                Caption (same across all selected channels)
              </span>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={10}
                disabled={busy}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,.10)",
                  background: "rgba(255,255,255,.04)",
                  color: "var(--ql-fg)",
                  fontSize: 13,
                  lineHeight: 1.6,
                  fontFamily: "var(--ql-font-mono)",
                  outline: "none",
                  resize: "vertical",
                  minHeight: 200,
                }}
              />
            </label>

            {/* Result summary */}
            {results && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "rgba(123,211,137,.06)",
                  border: "1px solid rgba(123,211,137,.20)",
                  fontSize: 11,
                  color: "var(--ql-fg-muted)",
                  fontFamily: "var(--ql-font-mono)",
                }}
              >
                <div
                  style={{
                    color: "#7BD389",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                  }}
                >
                  Publish result
                </div>
                {results.map((r) => (
                  <div key={r.channelId} style={{ color: r.error ? "var(--ql-danger)" : "var(--ql-fg)" }}>
                    {r.service} · {r.channelName ?? r.channelId} —{" "}
                    {r.error ? `FAILED: ${r.error}` : `OK (${r.postId?.slice(0, 8)}…)`}
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "rgba(248,113,113,.08)",
                  border: "1px solid rgba(248,113,113,.30)",
                  color: "var(--ql-danger)",
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: isMobile ? "12px 14px" : "14px 22px",
            borderTop: "1px solid rgba(255,255,255,.06)",
            background: "rgba(10,9,8,.6)",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <span
            style={{
              marginRight: "auto",
              fontSize: 10,
              letterSpacing: ".10em",
              textTransform: "uppercase",
              color: "var(--ql-fg-subtle)",
              fontFamily: "var(--ql-font-mono)",
            }}
          >
            {selected.size > 0
              ? `${selected.size} channel${selected.size > 1 ? "s" : ""} selected`
              : "Pick at least one channel"}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg)",
              fontSize: 12,
              fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handlePublish()}
            disabled={busy || selected.size === 0 || !caption.trim() || (!publishNow && !dueAt)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid rgba(123,211,137,.40)",
              background: "rgba(123,211,137,.18)",
              color: "#7BD389",
              fontSize: 12,
              fontWeight: 700,
              cursor: busy ? "wait" : "pointer",
              fontFamily: "inherit",
              opacity:
                busy || selected.size === 0 || !caption.trim() || (!publishNow && !dueAt) ? 0.55 : 1,
            }}
          >
            {busy ? <Loader2 size={13} className="ql-spin" /> : <Send size={13} strokeWidth={2} />}
            {busy
              ? publishNow
                ? "Posting now…"
                : "Scheduling…"
              : (() => {
                  const n = selected.size;
                  const label = n === 1 ? "1 channel" : `${n} channels`;
                  if (n === 0) return "Pick a channel";
                  return publishNow ? `Post now to ${label}` : `Schedule to ${label}`;
                })()}
          </button>
        </div>
      </div>
    </div>
  );
}

function whenBtn(active: boolean, busy: boolean): React.CSSProperties {
  return {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 8,
    border: `1px solid ${active ? "rgba(123,211,137,.40)" : "rgba(255,255,255,.10)"}`,
    background: active ? "rgba(123,211,137,.10)" : "rgba(255,255,255,.03)",
    color: active ? "#7BD389" : "var(--ql-fg-muted)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    cursor: busy ? "not-allowed" : "pointer",
    fontFamily: "inherit",
  };
}

function btnGhost(active: boolean): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,.10)",
    background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.03)",
    color: "var(--ql-fg)",
    fontSize: 12,
    fontWeight: 600,
    cursor: active ? "wait" : "pointer",
    fontFamily: "inherit",
  };
}

function chevronStyle(side: "left" | "right"): React.CSSProperties {
  return {
    position: "absolute",
    [side]: 8,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 4,
    width: 36,
    height: 36,
    borderRadius: 999,
    border: 0,
    background: "rgba(0,0,0,.55)",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    backdropFilter: "blur(4px)",
    boxShadow: "0 2px 8px rgba(0,0,0,.4)",
    transition: "background .15s, transform .15s",
  };
}

// Matches the AI-image card's timeAgo format exactly ("just now / Nm / Nh / Nd ago").
function fmtRel(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}
